import React, { useMemo } from 'react'

import { twMerge } from 'tailwind-merge'

export type BadgeVariant =
  | 'default'
  | 'danger'
  | 'soft'
  | 'accent'
  | 'positive'
  | 'blue'

interface Badge {
  count: number | string
  limit?: number
  variant?: BadgeVariant
  className?: string
  style?: React.CSSProperties
}

interface BadgeIndicator {
  variant?: BadgeVariant
  className?: string
}

export const MiniBadge: React.FC<Badge> = ({
  count,
  limit = 9,
  className = '',
  variant = 'default',
}) => {
  const variantClassName = useMemo(() => {
    switch (variant) {
      case 'danger':
        return 'text-white dark:text-white font-bold bg-red-600 dark:bg-red-500'
      case 'soft':
        return 'text-primary-700 dark:text-primary-100 font-semibold bg-primary-300 dark:bg-primary-600'
      case 'accent':
        return 'text-white dark:text-white font-bold bg-accent-500 dark:bg-accent-400'
      case 'positive':
        return 'text-white dark:text-white font-bold bg-green-600 dark:bg-green-500'
      case 'blue':
        return 'text-white dark:text-white font-bold bg-blue-600 dark:bg-blue-500'
      default:
        return 'text-primary-700 dark:text-primary-100 font-semibold bg-primary-200 dark:bg-primary-600'
    }
  }, [variant])

  return (
    <span
      style={{ minWidth: '14px' }}
      className={twMerge(
        'flex items-center justify-center text-[8px] rounded-full h-[14px] w-fit px-[1px]',
        variantClassName,
        className,
      )}
    >
      {typeof count === 'number' && count > limit ? `${limit}+` : count}
    </span>
  )
}

export const BadgeIndicator: React.FC<BadgeIndicator> = ({
  className = '',
  variant = 'default',
}) => {
  const variantClassName = useMemo(() => {
    switch (variant) {
      case 'danger':
        return 'bg-red-600 dark:bg-red-500'
      case 'soft':
        return 'bg-primary-300 dark:bg-primary-600'
      case 'accent':
        return 'bg-accent-500 dark:bg-accent-400'
      case 'positive':
        return 'bg-green-600 dark:bg-green-500'
      case 'blue':
        return 'bg-blue-600 dark:bg-blue-500'
      default:
        return 'bg-primary-200 dark:bg-primary-600'
    }
  }, [variant])
  return (
    <div
      className={twMerge(
        'rounded-full w-[10px] h-[10px]',
        variantClassName,
        className,
      )}
    />
  )
}

const Badge: React.FC<Badge> = ({
  count,
  className = '',
  variant = 'default',
  style,
}) => {
  const variantClassName = useMemo(() => {
    switch (variant) {
      case 'danger':
        return 'text-white font-medium dark:text-white bg-red-600 dark:bg-red-500'
      case 'soft':
        return 'text-primary-700 dark:text-primary-100 bg-primary-300 dark:bg-primary-600'
      case 'accent':
        return 'text-white dark:text-white bg-accent-500 dark:bg-accent-400'
      case 'positive':
        return 'text-white dark:text-white bg-green-600 dark:bg-green-500'
      case 'blue':
        return 'text-white dark:text-white bg-blue-600 dark:bg-blue-500'
      default:
        return 'text-primary-700 dark:text-primary-100 bg-primary-200 dark:bg-primary-600'
    }
  }, [variant])

  if (count === 0 || count === '') {
    return null
  }
  const isText = typeof count === 'string'
  return (
    <span
      style={{ minWidth: '20px', ...style }}
      className={twMerge(
        'flex items-center justify-center text-xs rounded-full w-fit h-5',
        isText ? 'px-2' : 'px-1',
        variantClassName,
        className,
      )}
    >
      {count}
    </span>
  )
}

export default Badge
